/**
 * ============================================================================
 * PayoutInformationCard - PAYOUT INFO WITH REQUEST BUTTON
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. THREE COLUMN LAYOUT
 *    - Available Balance (green) - Ready for payout
 *    - Held Balance (orange) - Waiting for booking completion
 *    - Reserved for Refunds (orange) - Held for pending refunds
 *
 * 2. REQUEST PAYOUT BUTTON
 *    - Only enabled if meets_minimum AND has eligible bookings
 *    - Shows loading state while processing
 *    - Parent handles mutation and shows result modal
 *
 * 3. VIEW BOOKINGS LINKS
 *    - Available balance: Click to see eligible bookings
 *    - Held balance: Click to see held bookings
 *    - Opens modals in parent component
 *
 * 4. MINIMUM THRESHOLD WARNING
 *    - Show yellow alert if below minimum
 *    - Explain how much more needed
 *    - Only show if balance exists but below threshold
 *
 * ============================================================================
 */

import React from 'react';
import { CheckCircle, Clock, Calendar, AlertCircle, FileText } from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils/revenueFormatters';
import type { PayoutInfo } from '../../types/revenue';

interface PayoutInformationCardProps {
  payoutInfo: PayoutInfo;
  onRequestPayout: () => void;
  requesting: boolean;
  onViewAvailable: () => void;
  onViewHeld: () => void;
  onViewReserved: () => void;
}

/**
 * Payout information card with available/held balances and request button
 */
export function PayoutInformationCard({
  payoutInfo,
  onRequestPayout,
  requesting,
  onViewAvailable,
  onViewHeld,
  onViewReserved,
}: PayoutInformationCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-xl font-bold text-gray-900 mb-5">Payout Information</h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Available Balance */}
        <div className="border-l-4 border-green-500 pl-4">
          <div className="flex items-center space-x-2 mb-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <p className="text-sm text-gray-600 font-medium">Available Balance</p>
          </div>
          <p className="text-[28px] font-semibold text-gray-900 leading-none mb-1">
            {formatCurrency(payoutInfo.available_balance)}
          </p>
          <p className="text-xs text-gray-500 mb-1">
            {payoutInfo.eligible_bookings_count} booking{payoutInfo.eligible_bookings_count !== 1 ? 's' : ''} ready
          </p>

          {payoutInfo.eligible_bookings_count > 0 && (
            <button
              onClick={onViewAvailable}
              className="text-xs text-blue-600 hover:text-blue-700 font-semibold mb-3 flex items-center space-x-1"
            >
              <FileText className="w-3 h-3" />
              <span>
                View {payoutInfo.eligible_bookings_count} booking{payoutInfo.eligible_bookings_count > 1 ? 's' : ''}
              </span>
            </button>
          )}

          <div className="mt-3 space-y-2">
            {payoutInfo.meets_minimum && payoutInfo.eligible_bookings_count > 0 ? (
              <button
                onClick={onRequestPayout}
                disabled={requesting}
                className="w-full bg-green-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-green-700 disabled:bg-gray-400 transition-colors"
              >
                {requesting ? 'Processing...' : 'Request Withdrawal'}
              </button>
            ) : (
              <button className="w-full bg-gray-100 text-gray-400 py-2.5 rounded-xl text-sm font-semibold cursor-not-allowed">
                Request Withdrawal
              </button>
            )}
          </div>
        </div>

        {/* Held Balance */}
        <div className="border-l-4 border-orange-500 pl-4">
          <div className="flex items-center space-x-2 mb-2">
            <Clock className="w-4 h-4 text-orange-600" />
            <p className="text-sm text-gray-600 font-medium">Held Balance</p>
          </div>
          <p className="text-[28px] font-semibold text-gray-900 leading-none mb-1">
            {formatCurrency(payoutInfo.held_balance)}
          </p>
          <p className="text-xs text-gray-500 mb-1">
            {payoutInfo.held_bookings_count} booking{payoutInfo.held_bookings_count !== 1 ? 's' : ''} held
          </p>

          {payoutInfo.held_bookings_count > 0 && (
            <button
              onClick={onViewHeld}
              className="text-xs text-blue-600 hover:text-blue-700 font-semibold mb-2 flex items-center space-x-1"
            >
              <FileText className="w-3 h-3" />
              <span>
                View {payoutInfo.held_bookings_count} booking{payoutInfo.held_bookings_count > 1 ? 's' : ''}
              </span>
            </button>
          )}

          <p className="text-xs text-orange-600 font-semibold">Released after booking ends</p>
        </div>

        {/* Reserved for Refunds */}
        <div className="border-l-4 border-orange-500 pl-4">
          <div className="flex items-center space-x-2 mb-2">
            <AlertCircle className="w-4 h-4 text-orange-600" />
            <p className="text-sm text-gray-600 font-medium">Reserved for Refunds</p>
          </div>
          <p className="text-[28px] font-semibold text-gray-900 leading-none mb-1">
            {formatCurrency(payoutInfo.reserved_for_refunds)}
          </p>
          <p className="text-xs text-gray-500 mb-1">
            {payoutInfo.reserved_refunds_count} pending refund{payoutInfo.reserved_refunds_count !== 1 ? 's' : ''}
          </p>

          {payoutInfo.reserved_refunds_count > 0 && (
            <button
              onClick={onViewReserved}
              className="text-xs text-blue-600 hover:text-blue-700 font-semibold mb-2 flex items-center space-x-1"
            >
              <FileText className="w-3 h-3" />
              <span>
                View {payoutInfo.reserved_refunds_count} refund{payoutInfo.reserved_refunds_count > 1 ? 's' : ''}
              </span>
            </button>
          )}

          <p className="text-xs text-orange-600 font-semibold">
            Held until refund{payoutInfo.reserved_refunds_count !== 1 ? 's are' : ' is'} processed
          </p>
        </div>
      </div>

      {/* Minimum Payout Warning */}
      {!payoutInfo.meets_minimum && payoutInfo.available_balance > 0 && (
        <div className="mt-5 bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-yellow-900">
              Minimum Payout: {formatCurrency(payoutInfo.minimum_threshold)}
            </p>
            <p className="text-xs text-yellow-700">
              Your available balance ({formatCurrency(payoutInfo.available_balance)}) is below the
              minimum threshold.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
